import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from 'reactstrap/lib/Button';
import colors from 'customizations/js/color-variables';
import { withCoreComponent } from 'core/hocs';
import { FormattedTag, CioFcmTracker } from 'core/components';
import { IconBell } from 'components/icons/icon-bell/icon-bell';
import { IconCloseThin } from 'components/icons/icon-close-thin/icon-close-thin';
import { NOTIFICATION_PERMISSION_TYPES } from 'src/constants';
import {
  askNotificationPermission,
  initializeFirebaseMessaging,
  isUserIdentifiedInCio,
  handleCioFcmInfo,
} from 'helpers/cio-fcm';

import './cio-fcm-prompt.scss';

const CioFcmPromptUI = ({
  userData,
  isUserLoggedIn,
  setCioFcmInfo,
}) => {
  const [isDisplayed, setIsDisplayed] = useState(true);

  const onPermissionGranted = () => {
    initializeFirebaseMessaging({
      partyId: userData.partyId,
      setCioFcmInfo,
      onTokenReceived: handleCioFcmInfo,
    });
  };

  function actionButtonClick({ currentTarget: { dataset: { value } } }) {
    setIsDisplayed(false);

    if (value === 'no') {
      return;
    }

    askNotificationPermission({
      onPermissionGranted,
    });
  }

  function onCloseBtnClick() {
    setIsDisplayed(false);
  }

  const shouldHidePrompt = () => (
    !('Notification' in window)
    || Notification.permission !== NOTIFICATION_PERMISSION_TYPES.DEFAULT
    || !isUserLoggedIn
    || !isUserIdentifiedInCio()
  );

  if (shouldHidePrompt()) {
    return null;
  }

  return (
    <div
      className={classNames(
        'cio-fcm-prompt flex-column bg-dark-gradient',
        'rounded position-fixed p-2_5 mx-1_5 mx-sm-2 mx-md-2_5',
        {
          'd-none': !isDisplayed,
          'd-flex': isDisplayed,
        }
      )}
    >
      <div className="d-flex justify-content-between">
        <div className="d-flex">
          <IconBell className="mr-0_75" />
          <FormattedTag
            tag="div"
            id="cio-fcm-prompt.title"
            className="h4 font-weight-semi-bold mr-2"
          />
        </div>

        <div
          role="button"
          tabIndex="0"
          onClick={onCloseBtnClick}
          onKeyPress={onCloseBtnClick}
          className="d-flex alighn-items-start p-0_5"
        >
          <IconCloseThin color={colors.gray20} className="close-btn-icon" />
        </div>
      </div>

      <div className="d-flex align-items-center">
        <div
          role="button"
          tabIndex="0"
          data-value="no"
          onClick={actionButtonClick}
          onKeyPress={actionButtonClick}
          className="mr-2_5"
        >
          <FormattedTag
            tag="div"
            id="cio-fcm-prompt.btn-no"
            className="text-gray-5"
          />
        </div>

        <FormattedTag
          id="cio-fcm-prompt.btn-yes"
          data-value="yes"
          tag={Button}
          color="primary"
          className="text-base px-1_25 py-0_5"
          onClick={actionButtonClick}
        />
      </div>
    </div>
  );
};

CioFcmPromptUI.propTypes = {
  userData: PropTypes.shape().isRequired,
  isUserLoggedIn: PropTypes.bool.isRequired,
  setCioFcmInfo: PropTypes.func.isRequired,
};

export const CioFcmPrompt = withCoreComponent(CioFcmTracker, CioFcmPromptUI);
